/* LEGEND — Momentum Vision pure draw path.
   Deliberately SELF-CONTAINED (zero imports) so it is benchmarkable outside
   the app: scripts/vision-bench.cjs compiles this file alone and measures the
   per-paint JS cost against a worst-case frame (24 objects + 12 levels). The
   VisionLayer component owns WHEN to paint (dirty-flag + animation throttle);
   this module owns WHAT a paint costs — keep it allocation-light and cache
   text measurement (measureText is the expensive canvas call; chip strings
   repeat across paints). */

export const VCLASS: Record<string, { label: string; color: string; engine?: boolean }> = {
  impulse: { label: 'Impulse', color: '#2dd4bf' },
  pullback: { label: 'Pullback', color: '#e0b15a' },
  higher_low: { label: 'Higher low', color: '#38c08a' },
  lower_high: { label: 'Lower high', color: '#f5883b' },
  base: { label: 'Base', color: '#4f8cff' },
  selling_stopped: { label: 'Selling stopped', color: '#a3e635' },
  BREAK_CONFIRM: { label: 'Break confirm', color: '#8b93a1', engine: true },
  ABSORPTION: { label: 'Absorption', color: '#b06b6b', engine: true },
  EXHAUSTION: { label: 'Exhaustion', color: '#b06b6b', engine: true },
  FLIP_FAIL: { label: 'Flip fail', color: '#ec6a6a', engine: true },
  STOP_RUN: { label: 'Stop run', color: '#8b93a1', engine: true },
  FLIP: { label: 'Flip', color: '#8b93a1', engine: true },
  TURN: { label: 'Turn', color: '#a3e635', engine: true },
  FEEDING: { label: 'Feeding', color: '#8b93a1', engine: true },
  RETREAT: { label: 'Retreat', color: '#b06b6b', engine: true },
  ignition: { label: 'Ignition', color: '#f5883b', engine: true },
  pbl: { label: 'Pullback sig', color: '#e0b15a', engine: true },
};
export const vColor = (c: string): string => (VCLASS[c] || { color: '#8b93a1' }).color;
export const vLabel = (c: string): string => (VCLASS[c] || { label: c }).label;

const STATE_STYLE: Record<string, { alpha: number }> = {
  forming: { alpha: 0.95 },
  confirmed: { alpha: 1 },
  failed: { alpha: 0.4 },
  expired: { alpha: 0.3 },
};

/* The one fact worth putting on the chip, per class. When MV-1 scored the
   object, the MODEL'S probability leads the chip — the operator asked for
   model output, not rules chatter (2026-08-28). */
export function chipFact(o: any): string {
  const p = typeof o.p === 'number' ? Math.round(o.p * 100) + '% · ' : '';
  return p + chipBase(o);
}

function chipBase(o: any): string {
  // Model-only display (operator, 2026-08-28): plain class names — the
  // rules-era facts ("1/3" bars-held counters, retrace ordinals) are gone
  // from the chip. The hover popover keeps the detail.
  switch (o.class) {
    case 'pullback': return 'PB';
    case 'impulse': return 'IMPULSE';
    case 'higher_low': return 'HL';
    case 'lower_high': return 'LH';
    case 'base': return 'BASE';
    case 'selling_stopped': return 'SS';
    default: return vLabel(o.class).toUpperCase();
  }
}

/** Chart timeframe → minutes, for snapping an object's ms to the pane's bar
 *  grid before `timeToCoordinate`.
 *
 *  The app's chart state uses BARE numbers for minutes ('1','3','5','15') and
 *  letter suffixes above that ('1H','4H','D','W') — see Terminal's
 *  `useStateT(... : '1')` and replay-source's `tf === '3' || tf === '5'`.
 *  The original regex demanded a trailing 'm' ('15m'), which nothing in the
 *  app ever produces, so EVERY timeframe except 1m and 1H fell through to 1.
 *  On a 5m pane that made X() ask for the coordinate of a one-minute instant
 *  that is not a bar, `timeToCoordinate` returned null, and every glyph path
 *  `continue`d — so vision markers drew on the 1m chart and nowhere else.
 *  ('1m'/'15m' still parse, so a caller using that spelling is unaffected.)
 *
 *  D and W deliberately fall through to 1: vision objects are 1m/3m/5m/15m
 *  structures, so they have no meaning on a daily or weekly pane, and the
 *  resulting null coordinate keeps them off it. Epoch-aligned week buckets
 *  start on a Thursday and would mis-snap anyway. */
export const tfMinutes = (tf: string): number => {
  const s = String(tf ?? '').trim();
  const m = /^([0-9]+)m?$/i.exec(s);
  if (m) return parseInt(m[1]!, 10);
  const h = /^([0-9]+)h$/i.exec(s);
  if (h) return parseInt(h[1]!, 10) * 60;
  return 1;
};

/** Does this frame need continuous animation (marching forming dashes)? */
export function hasForming(frame: any): boolean {
  if (!frame || !frame.objects) return false;
  for (const o of frame.objects) {
    if (o.source === 'vision' && o.state === 'forming' &&
        (o.class === 'impulse' || o.class === 'base' || o.class === 'lower_high')) return true;
  }
  return false;
}

function hexA(hex: string, a: number): string {
  const s = hex.replace('#', '');
  return 'rgba(' + parseInt(s.slice(0, 2), 16) + ',' + parseInt(s.slice(2, 4), 16) + ',' + parseInt(s.slice(4, 6), 16) + ',' + a + ')';
}

/* measureText cache — the strings repeat paint after paint; keyed font|text.
   Capped so a pathological label stream cannot grow it unbounded. */
const widthCache = new Map<string, number>();
const WIDTH_CACHE_CAP = 512;
function textW(ctx: any, font: string, text: string, stats: DrawStats): number {
  const key = font + '|' + text;
  const hit = widthCache.get(key);
  if (hit != null) { stats.cacheHits++; return hit; }
  ctx.font = font;
  const w = ctx.measureText(text).width;
  stats.measures++;
  if (widthCache.size >= WIDTH_CACHE_CAP) widthCache.clear();
  widthCache.set(key, w);
  return w;
}

const FONT_CHIP = '700 9px "IBM Plex Sans", sans-serif';
const FONT_LAMP = '700 6px "IBM Plex Mono", monospace';
const FONT_SUP = '700 8px "IBM Plex Mono", monospace';

export interface VisionCoord {
  X: (ms: number) => number | null;
  Y: (p: number) => number | null;
  /** Inverse of Y — lets the draw code work out WHICH edge an off-scale price
   *  fell off. Optional: an older caller without it simply keeps the previous
   *  drop-on-null behaviour. */
  yToPrice?: (y: number) => number | null;
  rightX: number;
}
export interface DrawnRect { o: any; x: number; y: number; w: number; h: number }
export interface DrawStats { fills: number; strokes: number; texts: number; measures: number; cacheHits: number }
export interface DrawOpts { hoverId: string | null; alpha: number; nowMs: number; wallMs: number }

/** Chips (text labels) only for what deserves the eye: forming objects and
 *  fresh resolutions. Older confirmed structures keep their shape — the
 *  popover still names them on hover. (First live session: half-hour-old
 *  HL/LH chips buried the tape.) */
const CHIP_RESOLVED_MS = 3 * 60_000;
function chipAllowed(o: any, wallMs: number): boolean {
  if (o.state === 'forming') return true;
  if (o.resolvedAt != null) return wallMs - o.resolvedAt < CHIP_RESOLVED_MS;
  return true; // no resolution stamp (engine objects handle their own path)
}

function roundRect(ctx: any, x: number, y: number, w: number, h: number, r: number): void {
  r = Math.min(r, Math.abs(w) / 2, Math.abs(h) / 2);
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(x, y, w, h, r);
  else { ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath(); }
}

/** Y for a price that may sit OUTSIDE the visible scale.
 *
 *  lightweight-charts' `priceToCoordinate` returns null off-scale, and every
 *  draw path used to `continue` on that — so a marker anchored outside the
 *  pane vanished with no trace and no way for the reader to know something had
 *  been suppressed. IMCC 2026-09-18: the operator entered at 4.80 while the
 *  surviving higher_low was pivoted at 2.93, 46 minutes back; nothing drew,
 *  and the level only reappeared at 5.5 when a nearer pivot took the slot.
 *
 *  Pin it to the edge it fell off instead. The rail badge still prints the
 *  TRUE price, so an edge-pinned marker can never be misread as a level it is
 *  not. Returns null only when the direction is genuinely unknowable, which is
 *  the one case where dropping it is still right.
 */
function edgeY(coord: VisionCoord, h: number, price: number): { y: number; off: boolean } | null {
  const y = coord.Y(price);
  if (y != null) return { y, off: false };
  if (!coord.yToPrice) return null;
  const a = coord.yToPrice(0);
  const b = coord.yToPrice(h);
  if (a == null || b == null) return null;
  const hi = Math.max(a, b);
  const lo = Math.min(a, b);
  if (price > hi) return { y: 3, off: true };
  if (price < lo) return { y: h - 3, off: true };
  return null;
}

/** Paint one frame. Pure: canvas ctx + coordinate fns in, hit rects + op
 *  stats out. Never reads clocks, DOM, or globals beyond the width cache. */
export function drawVisionFrame(
  ctx: any,
  // Unused, but positional — callers pass (ctx, width, height, ...). Renamed
  // for noUnusedParameters, which ts-jest enforces where the app tsconfig does
  // not; it blocked the first test to import this module.
  _w: number,
  h: number,
  coord: VisionCoord,
  frame: any,
  opts: DrawOpts,
): { rects: DrawnRect[]; stats: DrawStats } {
  const rects: DrawnRect[] = [];
  const stats: DrawStats = { fills: 0, strokes: 0, texts: 0, measures: 0, cacheHits: 0 };
  if (!frame || frame.warm === false) return { rects, stats };

  const { X, Y } = coord;
  // Clamp every drawn x to the TAPE EDGE (the frame's newest 1m bar), not the
  // pane margin: higher-TF bucket times extrapolate past the last candle into
  // whitespace, which floated chips in empty space on the first live session.
  const tapeX = frame.barMs != null ? X(frame.barMs) : null;
  const rightX = tapeX != null ? Math.min(coord.rightX, tapeX + 10) : coord.rightX;
  const A = opts.alpha;
  const hoverId = opts.hoverId;

  const placed: Array<{ x0: number; x1: number; y0: number; y1: number }> = [];
  const placeY = (lx: number, lw: number, yPref: number): number => {
    let y = yPref;
    for (let k = 0; k < 10; k++) {
      const clash = placed.some((p) => !(lx > p.x1 || lx + lw < p.x0 || y + 9 < p.y0 || y - 9 > p.y1));
      if (!clash) break;
      y += 17;
    }
    return y;
  };
  const chip = (x: number, ymid: number, text: string, bg: string, fg: string, anchor: 'left' | 'right', hot: boolean): number => {
    const tw = textW(ctx, FONT_CHIP, text, stats);
    const cw = tw + 12, ch = 15;
    const cx = anchor === 'right' ? x - cw : x, cy = ymid - ch / 2;
    ctx.font = FONT_CHIP;
    ctx.fillStyle = bg; roundRect(ctx, cx, cy, cw, ch, 4); ctx.fill(); stats.fills++;
    if (hot) { ctx.strokeStyle = fg; ctx.lineWidth = 1; ctx.setLineDash([]); roundRect(ctx, cx, cy, cw, ch, 4); ctx.stroke(); stats.strokes++; }
    ctx.fillStyle = fg; ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
    ctx.fillText(text, cx + 6, ymid + 0.5); stats.texts++;
    return cw;
  };
  const lampRow = (x: number, ymid: number, litFor: (k: string) => boolean, colFor: (k: string) => string) => {
    const keys = ['1m', '3m', '5m', '15m'], labels = ['1', '3', '5', '15'];
    ctx.font = FONT_LAMP; ctx.textBaseline = 'middle';
    let cx = x;
    keys.forEach((k, i) => {
      const lit = litFor(k), wq = i === 3 ? 13 : 10;
      const col = colFor(k);
      ctx.globalAlpha = A;
      ctx.fillStyle = lit ? hexA(col, 0.31) : 'rgba(255,255,255,0.05)';
      roundRect(ctx, cx, ymid - 6, wq, 12, 3); ctx.fill(); stats.fills++;
      ctx.fillStyle = lit ? col : 'rgba(255,255,255,0.28)'; ctx.textAlign = 'center';
      ctx.fillText(labels[i]!, cx + wq / 2, ymid + 0.5); stats.texts++;
      cx += wq + 2;
    });
    ctx.textAlign = 'left';
  };

  // Object labels live on the RIGHT RAIL, price-anchored with the price in
  // the text (operator, 2026-08-28: chips glued to bars collide with the
  // legend/candles on every zoom/pan — FLOOR/CEIL badges are the idiom the
  // eye already knows). Geometry (pivot glyphs, boxes) stays on-chart; the
  // words live on the rail with a leader to the price they describe.
  const fmtPx = (px: number): string => (px >= 1 ? px.toFixed(2) : px.toFixed(3));
  // Rail items are COLLECTED, then laid out once as a price-sorted ladder
  // (operator: per-row collision dodging reshuffled the stack on zoom).
  const railItems: Array<{ kind: 'obj' | 'pill'; price: number; o?: any; lv?: any; col?: string; hot?: boolean }> = [];
  const railBadge = (o: any, anchorPrice: number, col: string, hot: boolean): void => {
    railItems.push({ kind: 'obj', price: anchorPrice, o, col, hot });
  };
  const drawObjBadge = (it: any, ry: number): void => {
    const ayr = edgeY(coord, h, it.price); if (!ayr) return;
    const ay = ayr.y;
    const lab = chipFact(it.o) +
      (it.extraNames && it.extraNames.length ? '+' + it.extraNames.join('+') : '') +
      ' ' + fmtPx(it.price);
    const lw = textW(ctx, FONT_CHIP, lab, stats) + 12;
    const bx2 = Math.min(rightX + 6, coord.rightX - lw - 2);
    ctx.globalAlpha = A * 0.8;
    ctx.strokeStyle = hexA(it.col, 0.55); ctx.lineWidth = 1; ctx.setLineDash([]);
    ctx.beginPath(); ctx.moveTo(bx2 - 6, ay); ctx.lineTo(bx2 - 1, ry); ctx.stroke(); stats.strokes++;
    ctx.globalAlpha = A;
    chip(bx2, ry, lab, hexA(it.col, it.hot ? 0.34 : 0.22), it.col, 'left', it.hot);
    rects.push({ o: it.o, x: bx2 - 6, y: ry - 10, w: lw + 8, h: 20 });
  };

  for (const o of frame.objects || []) {
    // Pullback boxes are OFF (operator, 2026-09-29: the big yellow "price
    // supported here" box buries the candles and the swing labels).
    if (o.source !== 'engine' && o.class === 'pullback') continue;
    const col = vColor(o.class);
    const hot = hoverId === o.id;
    const ss = STATE_STYLE[o.state] || STATE_STYLE['confirmed']!;

    // Engine objects: glyph + label pill, never a box. An unresolvable time
    // means the fire is outside this chart's view — skip it entirely (the
    // old rightX fallback piled orphan pills on the chart's right edge).
    if (o.source === 'engine') {
      let ex = X(o.tEnd); if (ex == null) continue; ex = Math.min(rightX - 2, Math.max(24, ex));
      const ey = Y((o.priceHigh + o.priceLow) / 2); if (ey == null) continue;
      ctx.globalAlpha = A; ctx.strokeStyle = col; ctx.lineWidth = 1.6; ctx.setLineDash([]);
      if (o.veto) { ctx.beginPath(); ctx.moveTo(ex - 10, ey - 5); ctx.lineTo(ex - 4, ey); ctx.lineTo(ex - 10, ey + 5); ctx.stroke(); stats.strokes++; }
      else { ctx.beginPath(); ctx.moveTo(ex - 13, ey); ctx.lineTo(ex - 3, ey); ctx.stroke(); stats.strokes++; }
      const lab = vLabel(o.class).toUpperCase();
      const lw = textW(ctx, FONT_CHIP, lab, stats) + 12;
      const ly = placeY(ex - 18 - lw, lw, ey);
      chip(ex - 18, ly, lab, hexA(col, hot ? 0.34 : 0.18), col, 'right', hot);
      placed.push({ x0: ex - 18 - lw, x1: ex - 18, y0: ly - 9, y1: ly + 9 });
      rects.push({ o, x: ex - 18 - lw, y: ly - 10, w: lw + 18, h: 20 });
      continue;
    }

    // Glyph classes.
    if (o.class === 'higher_low' || o.class === 'selling_stopped' || o.class === 'lower_high') {
      // HL/LH glyphs anchor ON THE PIVOT BAR (tStart = the pivot's own bar,
      // rules classes.ts) — drawing at tEnd trailed the marker along the
      // structure's right edge, unmoored from the bar it names (operator,
      // 2026-08-28). SS keeps its window-end anchor.
      const wantsPivot = o.class !== 'selling_stopped';
      let gx = wantsPivot ? X(o.tStart) : X(o.tEnd);
      if (gx == null) gx = wantsPivot ? X(o.tEnd) : X(o.tStart);
      if (gx == null) continue;
      gx = Math.min(rightX - 4, Math.max(8, gx));
      const isLH = o.class === 'lower_high';
      // The EXACT pivot price (facts.pivot) — the episode's priceLow/High is
      // the box edge INCLUDING the tolerance band, which floated the glyph
      // off the bar (operator, 2026-08-28).
      const pivotPx = o.facts && typeof o.facts.pivot === 'number'
        ? o.facts.pivot
        : (isLH ? o.priceHigh : o.priceLow);
      const gyr = edgeY(coord, h, pivotPx); if (!gyr) continue;
      const gy = gyr.y;
      // An edge-pinned marker is dimmed: it says "this structure is live but
      // its pivot is outside your view", not "the level is here".
      ctx.globalAlpha = A * ss.alpha * (gyr.off ? 0.45 : 1);
      if (o.class === 'selling_stopped') {
        const yy = gy + 6; ctx.strokeStyle = col; ctx.lineWidth = 1.8;
        ctx.beginPath(); ctx.moveTo(gx - 6, yy - 4); ctx.lineTo(gx, yy + 1); ctx.lineTo(gx + 6, yy - 4); ctx.stroke(); stats.strokes++;
        ctx.beginPath(); ctx.moveTo(gx - 6, yy + 4); ctx.lineTo(gx + 6, yy + 4); ctx.stroke(); stats.strokes++;
      } else {
        const dir = isLH ? -1 : 1;
        // Hug the bar: a marker 13px off the pivot read as unanchored.
        const yy = gy + 5 * dir, sz = 6;
        ctx.beginPath(); ctx.moveTo(gx, yy - sz * dir); ctx.lineTo(gx - sz, yy + sz * 0.7 * dir); ctx.lineTo(gx + sz, yy + sz * 0.7 * dir); ctx.closePath();
        if (o.state === 'confirmed') { ctx.fillStyle = col; ctx.fill(); stats.fills++; }
        else { ctx.strokeStyle = col; ctx.lineWidth = 1.6; if (o.state === 'failed') ctx.setLineDash([3, 3]); ctx.stroke(); stats.strokes++; ctx.setLineDash([]); }
      }
      rects.push({ o, x: gx - 9, y: gy - 6, w: 18, h: 26 }); // glyph stays hoverable
      if (chipAllowed(o, opts.wallMs)) {
        railBadge(o, pivotPx, col, hot);
      }
      continue;
    }

    // Impulse: NEVER a box. A day-long forming 15m impulse on a runner spans
    // the whole pane — filled or outlined, it buries the tape (the mustard
    // slab of the first live session). The leg is drawn as what it is: a slim
    // diagonal from (tStart, low) to (tEnd, high) with the chip at the high.
    if (o.class === 'impulse') {
      let ix0 = X(o.tStart), ix1 = X(o.tEnd);
      const iyL = Y(o.priceLow), iyH = Y(o.priceHigh);
      if (iyL == null || iyH == null) continue;
      if (ix0 == null && ix1 == null) continue;
      if (ix0 == null) ix0 = 6; if (ix1 == null) ix1 = rightX;
      ix0 = Math.max(2, Math.min(rightX, ix0)); ix1 = Math.max(ix0 + 12, Math.min(rightX, ix1));
      ctx.globalAlpha = A * ss.alpha * (o.state === 'forming' ? 1 : 0.6);
      ctx.strokeStyle = col; ctx.lineWidth = hot ? 2.6 : 2;
      if (o.state === 'forming') { ctx.setLineDash([7, 5]); ctx.lineDashOffset = -(opts.nowMs / 55) % 12; } else ctx.setLineDash([]);
      ctx.beginPath(); ctx.moveTo(ix0, iyL); ctx.lineTo(ix1, iyH); ctx.stroke(); stats.strokes++;
      ctx.setLineDash([]); ctx.lineDashOffset = 0;
      // arrowhead at the leg high
      const ang = Math.atan2(iyH - iyL, ix1 - ix0);
      ctx.beginPath();
      ctx.moveTo(ix1, iyH);
      ctx.lineTo(ix1 - 8 * Math.cos(ang - 0.4), iyH - 8 * Math.sin(ang - 0.4));
      ctx.moveTo(ix1, iyH);
      ctx.lineTo(ix1 - 8 * Math.cos(ang + 0.4), iyH - 8 * Math.sin(ang + 0.4));
      ctx.stroke(); stats.strokes++;
      if (chipAllowed(o, opts.wallMs)) {
        railBadge(o, o.priceHigh, col, hot);
      }
      rects.push({ o, x: Math.min(ix0, ix1) - 4, y: Math.min(iyL, iyH) - 8, w: Math.abs(ix1 - ix0) + 8, h: Math.abs(iyL - iyH) + 16 });
      continue;
    }

    // Box classes: pullback / base (bounded extents by definition).
    let x0 = X(o.tStart), x1 = X(o.tEnd);
    const yH = Y(o.priceHigh), yL = Y(o.priceLow);
    if (yH == null || yL == null) continue;
    if (x0 == null && x1 == null) continue; // fully outside the view — draw nothing
    if (x0 == null) x0 = 6; if (x1 == null) x1 = rightX;
    x0 = Math.max(2, Math.min(rightX, x0)); x1 = Math.max(x0 + 16, Math.min(rightX, x1));
    const bx = x0, by = Math.min(yH, yL), bw = x1 - x0, bh = Math.abs(yL - yH);
    ctx.globalAlpha = A * ss.alpha;
    ctx.fillStyle = hexA(col, 0.1);
    roundRect(ctx, bx, by, bw, bh, 5); ctx.fill(); stats.fills++;
    ctx.strokeStyle = col; ctx.lineWidth = hot ? 2.2 : 1.5;
    if (o.state === 'forming') { ctx.setLineDash([6, 4]); ctx.lineDashOffset = -(opts.nowMs / 55) % 10; } else ctx.setLineDash([]);
    roundRect(ctx, bx, by, bw, bh, 5); ctx.stroke(); stats.strokes++;
    ctx.setLineDash([]); ctx.lineDashOffset = 0;
    if (o.state === 'failed' || o.state === 'expired') {
      ctx.strokeStyle = hexA(col, 0.7); ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(bx, by + bh); ctx.lineTo(bx + bw, by); ctx.stroke(); stats.strokes++;
    }
    if (chipAllowed(o, opts.wallMs)) {
      railBadge(o, o.class === 'base' ? o.priceHigh : o.priceLow, col, hot);
    }
    rects.push({ o, x: bx, y: by, w: bw, h: bh });
  }

  // Touch-tested levels only (the touch-gated honesty rule). One SELF-READING
  // pill per level in the margin right of the tape — [FAM] [1][3][5][15] [n/m]
  // on a dark backdrop with a leader tick to the level's price — instead of
  // the anonymous lamp confetti of the first live session. Nearest 5 only;
  // collision-managed; hoverable (the popover names family/price/per-TF).
  const FAM_SHORT: Record<string, string> = {
    vwap: 'VW', prior: 'PD', session: 'SES', flip: 'FLIP', retained: 'RET', wall: 'W', sr: 'SR',
  };
  // Exact-average labels (operator: "9MA, 21MA, 50MA — which is it?"):
  // the level's raw type names it — "5m_EMA21" → E21·5m, "1m_SMA200" →
  // S200·1m, "15m_SwingHigh" → SWH·15m. Family label is the fallback.
  const famShort = (f: string, type?: string): string => {
    if (type) {
      const ma = /^(\w+)_(EMA|SMA)(\d+)$/.exec(type);
      if (ma) return (ma[2] === 'EMA' ? 'E' : 'S') + ma[3] + '\u00b7' + ma[1]!.toLowerCase();
      const sw = /^(\w+)_Swing(High|Low)$/.exec(type);
      if (sw) return 'SW' + (sw[2] === 'High' ? 'H' : 'L') + '\u00b7' + sw[1]!.toLowerCase();
    }
    if (FAM_SHORT[f]) return FAM_SHORT[f]!;
    if (f.startsWith('ma-')) return 'MA\u00b7' + f.slice(3);
    if (f.startsWith('swing-')) return 'SW\u00b7' + f.slice(6);
    return f.toUpperCase().slice(0, 3);
  };
  // MA diet (operator, 2026-08-28: four MA pills within 12 cents): the only
  // actionable MAs are the nearest one BELOW price (dynamic support) and the
  // nearest one ABOVE (dynamic cap) — the engine's own side classification
  // provides that without a price probe. Other families are untouched.
  const isMaFam = (f: string): boolean => (f || '').startsWith('ma-');
  let bestMaSup: any = null;
  let bestMaRes: any = null;
  for (const lv of frame.levels || []) {
    if (!isMaFam(lv.family)) continue;
    if (lv.side === 'support') { if (!bestMaSup || lv.price > bestMaSup.price) bestMaSup = lv; }
    else { if (!bestMaRes || lv.price < bestMaRes.price) bestMaRes = lv; }
  }
  // Model-read exemption: a decisive read (strong hold ≥.8 or paper ≤.35)
  // keeps its pill even if the MA diet would drop it — the operator must see
  // the model's loudest opinions regardless of family housekeeping.
  const decisive = (lv: any): boolean => typeof lv.p === 'number' && (lv.p >= 0.8 || lv.p <= 0.35);
  const pillLevels = (frame.levels || []).filter((lv: any) =>
    !isMaFam(lv.family) || lv === bestMaSup || lv === bestMaRes || decisive(lv));
  // Slots go to the most decisive reads first, not frame arrival order —
  // otherwise a 97% shelf can lose its pill to a mid-band level.
  pillLevels.sort((a: any, b: any) => {
    const da = typeof a.p === 'number' ? Math.abs(a.p - 0.5) : -1;
    const dbb = typeof b.p === 'number' ? Math.abs(b.p - 0.5) : -1;
    return dbb - da;
  });
  let shown = 0;
  const shownPrices: number[] = [];
  for (const lv of pillLevels) {
    if (shown >= 5) break;
    const per = lv.perTf || {};
    let anyTested = false;
    for (const k in per) { if (per[k] && per[k].status !== 'untested') { anyTested = true; break; } }
    if (!anyTested) continue;
    // MA-glue collapse (the ladder's own lesson): levels within the engine's
    // 0.3% same-level band are ONE price zone — one pill, not a stamped-over
    // stack (the 0.626 cluster blur of the first live session).
    if (shownPrices.some((p) => Math.abs(p - lv.price) / Math.max(lv.price, 1e-9) <= 0.003)) continue;
    if (Y(lv.price) == null) continue;
    shown++;
    shownPrices.push(lv.price);
    railItems.push({ kind: 'pill', price: lv.price, lv });
  }

  const drawPill = (it: any, rowY: number): void => {
    const lv = it.lv;
    const per = lv.perTf || {};
    const ly = Y(lv.price); if (ly == null) return;
    const fam = famShort(lv.family || 'sr', lv.type);
    // The PRICE rides the pill — entries get pinned to numbers, not lamps
    // (operator, mobile 2026-08-28). Model P(hold) follows when scored.
    const pxTxt = fmtPx(lv.price);
    // Model number policy (mirrors the ladder card): families measured
    // miscalibrated show NO number; the rest are banded so the read is
    // visible at a glance, not buried in digits.
    const P_HIDE = new Set(['wall', 'retained', 'session', 'flip']);
    const pShown = typeof lv.p === 'number' && !P_HIDE.has(lv.family) ? lv.p : null;
    const band = pShown == null ? null : pShown >= 0.8 ? 'strong' : pShown >= 0.5 ? 'hold' : pShown <= 0.35 ? 'paper' : null;
    const bandTone = band && band !== 'paper' ? (lv.side === 'support' ? '#38c08a' : '#ec6a6a') : '#9aa0aa';
    const sup = (lv.supported || '') + (pShown != null ? ' ' + Math.round(pShown * 100) + '%' : '');
    ctx.font = FONT_SUP;
    const famW = textW(ctx, FONT_SUP, fam, stats);
    const pxW = textW(ctx, FONT_SUP, pxTxt, stats);
    const supW = textW(ctx, FONT_SUP, sup, stats);
    const lampsW = 10 + 2 + 10 + 2 + 10 + 2 + 13;
    const pillW = 6 + famW + 4 + pxW + 5 + lampsW + 5 + supW + 6;
    const pillX = Math.min(rightX + 6, coord.rightX - pillW - 2);

    // leader tick from the level's true Y to the ladder row.
    ctx.globalAlpha = A * 0.8;
    ctx.strokeStyle = 'rgba(255,255,255,0.35)'; ctx.lineWidth = 1; ctx.setLineDash([]);
    ctx.beginPath(); ctx.moveTo(pillX - 6, ly); ctx.lineTo(pillX - 1, rowY); ctx.stroke(); stats.strokes++;

    ctx.globalAlpha = A;
    ctx.fillStyle = 'rgba(10,11,13,0.72)';
    roundRect(ctx, pillX, rowY - 9, pillW, 18, 5); ctx.fill(); stats.fills++;
    if (band === 'strong' || band === 'paper') {
      // strong hold: solid tone ring · paper: dashed grey ring ("fake wall")
      ctx.strokeStyle = band === 'strong' ? bandTone : 'rgba(154,160,170,0.75)';
      ctx.lineWidth = 1;
      ctx.setLineDash(band === 'paper' ? [3, 2] : []);
      roundRect(ctx, pillX, rowY - 9, pillW, 18, 5); ctx.stroke(); ctx.setLineDash([]); stats.strokes++;
    }
    ctx.font = FONT_SUP; ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.fillText(fam, pillX + 6, rowY + 0.5); stats.texts++;
    ctx.fillStyle = band === 'paper' ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.85)';
    ctx.fillText(pxTxt, pillX + 6 + famW + 4, rowY + 0.5); stats.texts++;
    if (band === 'paper') {
      // strike the price: the model says this wall doesn't hold
      ctx.strokeStyle = 'rgba(255,255,255,0.55)'; ctx.lineWidth = 1; ctx.setLineDash([]);
      ctx.beginPath(); ctx.moveTo(pillX + 6 + famW + 4, rowY + 0.5);
      ctx.lineTo(pillX + 6 + famW + 4 + pxW, rowY + 0.5); ctx.stroke(); stats.strokes++;
    }
    lampRow(pillX + 6 + famW + 4 + pxW + 5, rowY,
      (k) => per[k] != null && per[k].status !== 'untested',
      (k) => {
        const st = per[k] ? per[k].status : 'untested';
        return st === 'failed' ? '#ec6a6a' : st === 'held' ? '#2dd4bf' : '#38c08a';
      });
    const good = sup.length && sup[0] !== '0';
    ctx.font = FONT_SUP; ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
    // The model's number carries the color now (band tone); the old
    // supported-count green survives only when no model read is shown.
    ctx.fillStyle = band ? bandTone : good ? '#8ee6c0' : 'rgba(255,255,255,0.55)';
    ctx.fillText(sup, pillX + 6 + famW + 4 + pxW + 5 + lampsW + 5, rowY + 0.5); stats.texts++;

    rects.push({ o: { kind: 'level', id: 'lvl-' + lv.levelId, level: lv }, x: pillX, y: rowY - 10, w: pillW, h: 20 });
  };

  // ── The rail ladder: sort by price, place monotonically. Zoom compresses
  // or spreads the stack; it can never reorder or reshuffle it.
  // Agreement collapses (operator: six rows all reading "2.88" is ONE
  // statement): structure badges whose anchors sit within 0.4% merge into
  // the strongest voice, "+N" carrying how many other classes agree — the
  // hover popover still names each. Level pills stay individual.
  const CLS_ABBR: Record<string, string> = {
    impulse: 'IMP', pullback: 'PB', higher_low: 'HL', lower_high: 'LH',
    base: 'BASE', selling_stopped: 'SS',
  };
  const objItems = railItems.filter((i) => i.kind === 'obj')
    .sort((a, b) => ((b.o && b.o.p) || 0) - ((a.o && a.o.p) || 0));
  const keptObjs: any[] = [];
  for (const it of objItems) {
    const home = keptObjs.find((k) => Math.abs(k.price - it.price) / Math.max(it.price, 1e-9) <= 0.004);
    if (home) {
      // Name what merged — "+1" hid the pullback inside the base badge
      // (operator: "why does the PB box never show").
      (home.extraNames = home.extraNames || []).push(CLS_ABBR[it.o.class] || it.o.class);
      continue;
    }
    keptObjs.push(it);
  }
  // Level pills are OFF (operator, 2026-09-18). They were a second, cramped
  // copy of the structure rail: the rail already clusters the same levels into
  // zones, merges daily/weekly swings into shelves, joins the model's P(hold)
  // per zone (`mvAnnotate`, ranked by |p-0.5| so a confident "breaks" counts
  // as loudly as a confident "holds") and guarantees a floor and a ceiling.
  // The pill strip could do none of that and competed with the candles for
  // room. Object badges (IMP / PB / HL / LH / BASE / SS) stay — the rail has
  // no equivalent for those.
  //
  // Kept behind a flag rather than deleted: `drawPill` is still the renderer
  // if this is ever reversed, and the hover popover in VisionLayer is keyed to
  // the `kind: 'level'` rects it pushes.
  const SHOW_LEVEL_PILLS = false;
  const railFinal = [
    ...(SHOW_LEVEL_PILLS ? railItems.filter((i) => i.kind === 'pill') : []),
    ...keptObjs,
  ];

  const laid: Array<{ it: any; ay: number; y: number }> = [];
  for (const it of railFinal) {
    const ay = Y(it.price);
    if (ay != null) laid.push({ it, ay, y: ay });
  }
  laid.sort((a, b) => a.ay - b.ay);
  const ROW = 19;
  for (let i = 1; i < laid.length; i++) laid[i]!.y = Math.max(laid[i]!.y, laid[i - 1]!.y + ROW);
  const maxY = h - 12;
  for (let i = laid.length - 1; i >= 0; i--) {
    const cap = i === laid.length - 1 ? maxY : laid[i + 1]!.y - ROW;
    if (laid[i]!.y > cap) laid[i]!.y = cap;
  }
  for (const row of laid) {
    if (row.it.kind === 'obj') drawObjBadge(row.it, row.y);
    else drawPill(row.it, row.y);
  }
  ctx.globalAlpha = 1; ctx.setLineDash([]);
  return { rects, stats };
}
